// Profile page.
//
// Shape (UX Phase 1 rev-2, 2026-08-08): ONE scrolling page, and — the key fix —
// everything about the PERSON lives in a SINGLE "Personal details" card:
// name, phone, date of birth, gender, preferred language, and location. Tapping
// Edit turns that same card into one form with ONE Save button that updates all
// of it at once. No per-field cards, no per-field edit buttons, no tabs.
//
//   ── My details ──          Personal details (name · phone · DOB · gender ·
//                             language · location — one card, one Edit, one Save)
//   ── Sign-in & security ──  Password · Telegram · Log out
//
// i18n (UX Phase 9, 2026-08-08): every user-facing string now goes through
// t() — the Phase-1 rewrite had regressed this page to hardcoded English,
// dropping the translations non-English players used to see. Reuses the
// shared common.*/register.*/profile.* keys and adds profile.*/wallet.* keys
// (12-language coverage in shared-ui/src/lib/translations.ts).
//
// (Saved UPI moved to the Wallet page — it's payment, not identity. Referral
// stays as a highlight at the top for now; it moves to Rewards in Phase 2.)
import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { api, ApiError, User } from '@baaligames/shared-ui/lib/api';
import { compressForUpload } from '@baaligames/shared-ui/lib/imageCompress';
import { useAuth } from '@baaligames/shared-ui/lib/auth';
import { useI18n, useT } from '@baaligames/shared-ui/lib/i18n';
import { SUPPORTED_LANGS } from '@baaligames/shared-ui/lib/translations';
import type { Lang } from '@baaligames/shared-ui/lib/translations';
import { useToast } from '@baaligames/shared-ui/lib/toast';
import DobPicker from '@baaligames/shared-ui/components/DobPicker';
import LogoutButton from '@baaligames/shared-ui/components/LogoutButton';
import Icon from '@baaligames/shared-ui/components/Icon';
import AvatarCropper from '@baaligames/shared-ui/components/AvatarCropper';
import DangerConfirmModal from '@baaligames/shared-ui/components/DangerConfirmModal';
import LocationCapture, {
  type GeoBag,
  isGeoLocated,
} from '@baaligames/shared-ui/components/LocationCapture';

// Stored gender enum → i18n key. Handles both the form value ('prefer_not')
// and the canonical stored value ('prefer_not_to_say').
const GENDER_KEY: Record<string, string> = {
  male: 'profile.gender.male',
  female: 'profile.gender.female',
  other: 'profile.gender.other',
  prefer_not: 'profile.gender.preferNot',
  prefer_not_to_say: 'profile.gender.preferNot',
};
const GENDER_VALUES = ['male', 'female', 'other', 'prefer_not'] as const;

// Friendly label for the stored lowercase gender enum, via t().
function genderText(t: (k: string) => string, g?: string | null): string {
  if (!g) return '—';
  const k = GENDER_KEY[g];
  return k ? t(k) : g.charAt(0).toUpperCase() + g.slice(1);
}

export default function Profile() {
  const { user, refreshMe } = useAuth();
  const t = useT();
  if (!user) return null;
  return (
    <div className="space-y-3 max-w-2xl mx-auto pb-8">
      <AvatarHeader user={user} onUpdated={refreshMe} />

      {/* ── Group 1: My details ── */}
      <GroupLabel icon="person">{t('profile.group.myDetails')}</GroupLabel>
      <PersonalDetailsCard user={user} onUpdated={refreshMe} />
      {/* Language is its own tap-to-open picker (choose → saves → closes).
          It's the ONE saved language; the top-bar globe only changes it
          temporarily until "Save permanently" is pressed there. */}
      <PreferredLanguageCard user={user} onUpdated={refreshMe} />
      {/* Phase B (contract B6): one switch — "Show my name & photo when I
          win". Backed by users.board_visibility / board_photo; saves on tap. */}
      <BoardVisibilityCard />

      {/* ── Group 2: Sign-in & security ─────────────────────────── */}
      <GroupLabel icon="lock">{t('profile.group.security')}</GroupLabel>
      <ChangePasswordCard />
      <TelegramCard user={user} onUpdated={refreshMe} />
      <LogoutButton />
    </div>
  );
}

// Small section divider that names each group of cards.
function GroupLabel({ icon, children }: { icon: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 pt-3 pb-0.5 px-1">
      <Icon name={icon} size={16} className="text-neutral-500 dark:text-neon-200/70" />
      <h2 className="text-[11px] font-bold uppercase tracking-[.18em] text-neutral-500 dark:text-neon-200/70">
        {children}
      </h2>
    </div>
  );
}

// ─── The unified Personal details card ─────────────────────────────
//
// Read mode: every fact about the person on ONE card (name, phone, DOB,
// gender, preferred language, location) + a single Edit button.
// Edit mode: the SAME card becomes one form with all those fields and ONE
// Save button. Everything is a single PATCH /api/me (name/phone/dob/gender/
// geo all live on the user row), so one Save persists the lot. Location is
// optional here — it never blocks the Save.
type DetailsForm = {
  first_name: string; last_name: string; birthdate: string; gender: string; phone: string;
};

// Strip a stored "+91XXXXXXXXXX" down to the raw 10 digits for the input.
const strip91 = (p: string | null | undefined) =>
  (p || '').replace(/^\+?91/, '').replace(/\D/g, '').slice(0, 10);

// Does the user already have a saved location worth showing as a summary?
// Mirrors LocationCapture's own hasSavedLocation rule (full state/district/
// area triple OR a lat/lng pair) plus a saved pincode.
function hasSavedGeo(u: User): boolean {
  return (
    !!(u.area_id || u.pincode_id) ||
    (u.latitude != null && u.longitude != null) ||
    (!!u.state_id && !!u.district_id)
  );
}

function PersonalDetailsCard({ user, onUpdated }: { user: User; onUpdated: () => Promise<void> }) {
  const { push } = useToast();
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const initialForm: DetailsForm = useMemo(() => ({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    birthdate: user.birthdate || '',
    gender: user.gender || '',
    phone: strip91(user.phone),
  }), [user]);
  const [form, setForm] = useState<DetailsForm>(initialForm);

  const initialGeo: GeoBag = useMemo(() => ({
    latitude:    user.latitude    != null ? Number(user.latitude)  : null,
    longitude:   user.longitude   != null ? Number(user.longitude) : null,
    state_id:    user.state_id    ?? null,
    district_id: user.district_id ?? null,
    area_id:     user.area_id     ?? null,
    pincode_id:  user.pincode_id  ?? null,
    pincode:     (user as unknown as { pincode?: string | null }).pincode ?? null,
    nation:      user.nation || 'India',
  }), [user]);
  const [geo, setGeo] = useState<GeoBag>(initialGeo);

  function startEdit() {
    setForm(initialForm);
    setGeo(initialGeo);
    setEditing(true);
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (form.phone && form.phone.length !== 10) {
      push('error', t('profile.err.phone'));
      return;
    }
    setBusy(true);
    // One PATCH /me with every changed field — one Save, everything saved.
    const patch: Record<string, unknown> = {};
    if (form.first_name !== '') patch.first_name = form.first_name;
    if (form.last_name  !== '') patch.last_name  = form.last_name;
    if (form.birthdate  !== '') patch.birthdate  = form.birthdate;
    if (form.gender     !== '') patch.gender     = form.gender;
    if (form.phone.length === 10) patch.phone    = '+91' + form.phone;

    // Location is OPTIONAL — only include it when a real place is picked;
    // never block saving the other fields on it.
    if (isGeoLocated(geo)) {
      if (geo.latitude    !== undefined) patch.latitude    = geo.latitude;
      if (geo.longitude   !== undefined) patch.longitude   = geo.longitude;
      if (geo.state_id    !== undefined) patch.state_id    = geo.state_id;
      if (geo.district_id !== undefined) patch.district_id = geo.district_id;
      if (geo.area_id     !== undefined) patch.area_id     = geo.area_id;
      if (geo.pincode_id  !== undefined) patch.pincode_id  = geo.pincode_id;
      if (geo.nation)                    patch.nation      = geo.nation;
    }

    try {
      if (Object.keys(patch).length > 0) await api.patch('/me', patch);
      await onUpdated();
      push('success', t('profile.toast.detailsSaved'));
      setEditing(false);
    } catch (err) {
      push('error', (err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  // ── READ: one card, everything about the person ──
  if (!editing) {
    const fmtDob = user.birthdate
      ? (() => { const [y,m,d] = user.birthdate!.split('-'); return `${d}/${m}/${y}`; })()
      : '—';
    const name = [user.first_name, user.last_name].filter(Boolean).join(' ') || '—';
    return (
      <div className="card space-y-3 p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold">{t('profile.personal.title')}</h3>
          <button className="btn-primary text-xs px-3 py-1.5" onClick={startEdit}>{t('common.edit')}</button>
        </div>
        <dl className="grid grid-cols-1 gap-2.5 text-sm">
          <Row k={t('profile.field.name')} v={name} />
          <Row k={t('profile.field.phone')} v={user.phone || '—'} />
          <Row k={t('register.dob')} v={fmtDob} />
          <Row k={t('profile.field.gender')} v={genderText(t, user.gender)} />
          <div>
            <dt className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">{t('profile.field.location')}</dt>
            <dd>
              {hasSavedGeo(user)
                ? <LocationCapture
                    value={initialGeo}
                    onChange={() => {}}
                    mode="self"
                    showSavedSummary
                    hideSummaryEdit
                    disabled
                    inputClassName="input"
                  />
                : <span className="font-medium">—</span>}
            </dd>
          </div>
        </dl>
      </div>
    );
  }

  // ── EDIT: the same card, one form, one Save ──
  return (
    <form onSubmit={save} className="card space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold">{t('profile.personal.editTitle')}</h3>
        <button type="button" className="btn-ghost text-xs px-3 py-1.5"
                onClick={() => setEditing(false)} disabled={busy}>{t('common.cancel')}</button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="label">{t('register.firstName')}</label>
          <input className="input" value={form.first_name}
                 onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
        </div>
        <div>
          <label className="label">{t('register.lastName')}</label>
          <input className="input" value={form.last_name}
                 onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
        </div>
      </div>

      <div>
        <label className="label">{t('profile.field.phone')}</label>
        <div className="relative">
          <span className="absolute top-1/2 left-3 -translate-y-1/2 text-sm font-bold
                           text-arena-700 dark:text-neon-200/80 pointer-events-none select-none">
            +91
          </span>
          <input
            className="input pl-12 tabular-nums tracking-wider"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
            placeholder="9876543210"
            inputMode="numeric"
            pattern="[0-9]{10}"
            maxLength={10}
            autoComplete="tel-national"
          />
        </div>
      </div>

      <div>
        <label className="label">{t('register.dob')}</label>
        <DobPicker value={form.birthdate} onChange={(iso) => setForm({ ...form, birthdate: iso })} />
      </div>

      <div>
        <label className="label">{t('profile.field.gender')}</label>
        <select className="input" value={form.gender}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}>
          <option value="">{t('profile.gender.select')}</option>
          {GENDER_VALUES.map((v) => <option key={v} value={v}>{t(GENDER_KEY[v])}</option>)}
        </select>
      </div>

      <div>
        <label className="label">{t('profile.field.location')}</label>
        <LocationCapture
          key={user.geo_updated_at ?? 'new'}
          value={geo}
          onChange={setGeo}
          mode="self"
          showSavedSummary
          disabled={busy}
          inputClassName="input"
        />
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
          {t('profile.location.optionalHint')}
        </p>
      </div>

      <button className="btn-primary w-full" disabled={busy}>{busy ? t('common.saving') : t('common.save')}</button>
    </form>
  );
}

// ─── Preferred language — the ONE saved language ───────────────────
//
// Collapsed: a row showing the current saved language. Tap it → the native-
// script grid opens (the same picker the top-bar globe uses). Pick one →
// it saves to the DB, applies to the whole app, and the picker closes,
// showing the chosen language. This is the ONLY place a language is saved
// permanently from the profile; the top-bar globe changes it only for the
// session (until "Save permanently" is pressed there).
function PreferredLanguageCard({ user, onUpdated }: { user: User; onUpdated: () => Promise<void> }) {
  const { push } = useToast();
  const { setLang } = useI18n();
  const t = useT();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const code = (user.language as Lang) || 'en';
  const meta = SUPPORTED_LANGS.find((l) => l.code === code) ?? SUPPORTED_LANGS[0];

  async function pick(next: Lang) {
    if (next === code) { setOpen(false); return; }
    setBusy(next);
    try {
      await api.patch('/me', { language: next });
      setLang(next);        // apply to the whole app immediately
      await onUpdated();     // refresh the user so the collapsed label updates
      push('success', t('profile.lang.saved'));
      setOpen(false);        // auto-close after choosing
    } catch (e) {
      push('error', (e as ApiError).message);
    } finally {
      setBusy(null);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="card w-full p-3 flex items-center gap-3 text-left active:scale-[.99] transition"
      >
        <div className="w-10 h-10 rounded-xl grid place-items-center
                        bg-gradient-to-br from-neon-300/40 to-brand-300/30
                        dark:from-neon-400/20 dark:to-brand-500/20
                        text-arena-700 dark:text-neon-300 shrink-0">
          <Icon name="language" size={20} filled />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[.2em] font-bold text-neutral-500 dark:text-neon-200/70">
            {t('profile.lang.title')}
          </div>
          <div className="font-bold truncate">
            {meta.native}
            <span className="text-xs font-medium text-neutral-500 dark:text-neon-200/60"> · {meta.english}</span>
          </div>
        </div>
        <Icon name="expand_more" size={22} className="text-neutral-400 dark:text-neon-200/60 shrink-0" />
      </button>
    );
  }

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-bold">{t('profile.lang.title')}</h3>
        <button type="button" className="btn-ghost text-xs px-3 py-1.5" onClick={() => setOpen(false)}>{t('common.close')}</button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {SUPPORTED_LANGS.map((l) => {
          const active = l.code === code;
          const isBusy = busy === l.code;
          return (
            <button
              key={l.code}
              type="button"
              onClick={() => !isBusy && pick(l.code)}
              disabled={busy !== null}
              aria-pressed={active}
              dir={l.code === 'ur' ? 'rtl' : 'ltr'}
              className={`relative text-left p-3 rounded-xl border transition active:scale-[.98]
                          ${active
                            ? 'border-neon-400 bg-neon-100/60 dark:bg-neon-400/10 shadow-neon-cyan'
                            : 'border-neutral-200 dark:border-arena-600/40 bg-white/60 dark:bg-arena-800/40 hover:border-neon-400/50'}
                          ${isBusy ? 'opacity-70' : ''}`}
            >
              <div className="font-display font-extrabold text-base leading-tight text-arena-800 dark:text-neon-100">
                {l.native}
              </div>
              <div className="text-[11px] uppercase tracking-wider text-neutral-500 dark:text-neon-200/60">
                {l.english}
              </div>
              {active && (
                <Icon name="check_circle" filled
                      className="absolute top-2 right-2 text-neon-600 dark:text-neon-300" size={18} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── "Show my name & photo when I win" (Phase B, contract B6) ─────────────
//
// ONE card, ONE switch, saves on tap — a switch that needs a second "Save"
// tap is a trap on a phone. Backed by users.board_visibility / board_photo
// (migration 164, plan §4.9): OFF = a masked name on winner boards and win
// cards (today's behaviour for everyone), ON = username + profile photo.
// The whole row is the tap target (≥ 60px tall); the pill is decorative.
// Until migration 164 is applied the server answers editable:false and the
// row reads "Coming soon" instead of failing.
type BoardPrefs = { visibility: 'masked' | 'named'; photo: boolean; editable: boolean };

function BoardVisibilityCard() {
  const { push } = useToast();
  const t = useT();
  // i18n keys resolve to themselves when missing; fall back to plain English
  // so a not-yet-translated key never renders raw.
  const tt = (key: string, fallback: string) => { const s = t(key); return s === key ? fallback : s; };
  const [prefs, setPrefs] = useState<BoardPrefs | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get<BoardPrefs>('/me/board-visibility')
      .then((p) => { if (!cancelled) setPrefs(p); })
      // A failed read must not break the profile page — show the safe default.
      .catch(() => { if (!cancelled) setPrefs({ visibility: 'masked', photo: false, editable: false }); });
    return () => { cancelled = true; };
  }, []);

  const on = prefs?.visibility === 'named';
  const editable = !!prefs?.editable;
  const disabled = !prefs || !editable || busy;

  async function toggle() {
    if (!prefs || disabled) return;
    const before = prefs;
    const next: BoardPrefs['visibility'] = on ? 'masked' : 'named';
    setBusy(true);
    setPrefs({ ...prefs, visibility: next, photo: next === 'named' }); // optimistic
    try {
      // The api client has no `put`; the server binds PATCH to the same handler.
      const saved = await api.patch<BoardPrefs>('/me/board-visibility', { visibility: next, photo: next === 'named' });
      setPrefs(saved);
      push('success', next === 'named'
        ? tt('profile.board.onToast', 'Your name and photo will show when you win.')
        : tt('profile.board.offToast', 'You will appear with a masked name.'));
    } catch (e) {
      setPrefs(before); // revert the optimistic flip
      push('error', (e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  const help = !prefs ? tt('common.loading', 'Loading…')
    : !editable ? tt('profile.board.comingSoon', 'Coming soon')
    : on ? tt('profile.board.onHelp', 'Winner boards show your username and photo.')
    : tt('profile.board.offHelp', 'Winner boards show a masked name, like Ra***.');

  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={tt('profile.board.title', 'Show my name & photo when I win')}
      disabled={disabled}
      onClick={toggle}
      className="card w-full p-3 flex items-center gap-3 text-left active:scale-[.99] transition
                 disabled:opacity-60 disabled:active:scale-100"
    >
      <div className="w-10 h-10 rounded-xl grid place-items-center
                      bg-gradient-to-br from-neon-300/40 to-brand-300/30
                      dark:from-neon-400/20 dark:to-brand-500/20
                      text-arena-700 dark:text-neon-300 shrink-0">
        <Icon name="emoji_events" size={20} filled />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold leading-tight">{tt('profile.board.title', 'Show my name & photo when I win')}</div>
        <div className="text-xs text-neutral-500 dark:text-neon-200/60 mt-0.5">{help}</div>
      </div>
      {/* decorative pill — the whole row is the control */}
      <span
        aria-hidden="true"
        className={`relative shrink-0 w-12 h-7 rounded-full transition-colors
                    ${on ? 'bg-neon-500 dark:bg-neon-400' : 'bg-arena-300 dark:bg-arena-600'}`}
      >
        <span
          className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white shadow transition-transform
                      ${on ? 'translate-x-5' : ''}`}
        />
      </span>
    </button>
  );
}

// Compact avatar + identity header.
function AvatarHeader({ user, onUpdated }: { user: User; onUpdated: () => Promise<void> }) {
  const { push } = useToast();
  const t = useT();
  const [preview, setPreview] = useState(false);
  const [cropping, setCropping] = useState(false);
  async function upload(rawFile: File) {
    try {
      const file = await compressForUpload(rawFile, { preset: 'avatar' });
      const fd = new FormData(); fd.append('avatar', file);
      await api.postForm('/me/avatar', fd);
      await onUpdated();
      push('success', t('profile.toast.photoUpdated'));
    } catch (e) { push('error', (e as ApiError).message); }
  }
  return (
    <>
      <div className="card flex items-center gap-3 p-3">
        <button onClick={() => user.profile_image_url && setPreview(true)}
          className="shrink-0 w-16 h-16 rounded-xl overflow-hidden bg-neutral-200 dark:bg-neutral-800 grid place-items-center">
          {user.profile_image_url
            ? <img src={user.profile_image_url} alt="" className="w-full h-full object-cover" />
            : <Icon name="person" size={28} className="text-neutral-500 dark:text-neutral-400" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-bold truncate">@{user.username}</div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{user.email}</div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{user.phone}</div>
        </div>
        <button
          type="button"
          onClick={() => setCropping(true)}
          className="btn-ghost text-xs px-3 py-2 shrink-0 inline-flex items-center gap-1"
        >
          <Icon name="photo_camera" size={14} /> {t('profile.photo')}
        </button>
      </div>

      {preview && user.profile_image_url && (
        <div className="fixed inset-0 z-50 bg-black/90 grid place-items-center p-4" onClick={() => setPreview(false)}>
          <img src={user.profile_image_url} className="max-w-full max-h-full rounded-xl" />
        </div>
      )}

      {cropping && (
        <AvatarCropper
          title={t('profile.avatar.cropTitle')}
          onUpload={upload}
          onClose={() => setCropping(false)}
        />
      )}
    </>
  );
}

// ─── Change password (in-session) ─────────────────────────────────
function ChangePasswordCard() {
  const { push } = useToast();
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);

  function reset() {
    setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    setShowCurrent(false); setShowNew(false);
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      push('error', t('profile.err.pwMismatch')); return;
    }
    if (newPassword.length < 8 || !/[A-Za-z]/.test(newPassword) || !/\d/.test(newPassword)) {
      push('error', t('profile.err.pwWeak')); return;
    }
    if (newPassword === currentPassword) {
      push('error', t('profile.err.pwSame')); return;
    }
    setBusy(true);
    try {
      await api.post('/me/password', { currentPassword, newPassword });
      push('success', t('profile.toast.pwChanged'));
      reset();
      setEditing(false);
    } catch (e) {
      push('error', (e as ApiError).message);
    } finally { setBusy(false); }
  }

  if (!editing) {
    return (
      <div className="card p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-bold mb-1">{t('profile.password.title')}</h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-300">
              {t('profile.password.desc')}
            </p>
          </div>
          <button className="btn-primary text-xs px-3 py-1.5 shrink-0"
                  onClick={() => { reset(); setEditing(true); }}>
            {t('profile.password.cta')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={save} className="card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-bold">{t('profile.password.editTitle')}</h3>
        <button type="button" className="btn-ghost text-xs px-3 py-1.5"
                onClick={() => { reset(); setEditing(false); }}
                disabled={busy}>{t('common.cancel')}</button>
      </div>
      <div>
        <label className="label">{t('profile.password.current')}</label>
        <div className="relative">
          <input
            className="input pr-12"
            type={showCurrent ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder={t('profile.password.currentPh')}
            autoComplete="current-password"
            required
          />
          <button type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-arena-600 dark:text-neon-200/80 font-semibold"
                  onClick={() => setShowCurrent((v) => !v)}>
            {showCurrent ? t('common.hide') : t('common.show')}
          </button>
        </div>
      </div>
      <div>
        <label className="label">{t('profile.password.new')}</label>
        <div className="relative">
          <input
            className="input pr-12"
            type={showNew ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder={t('profile.password.newPh')}
            autoComplete="new-password"
            minLength={8}
            maxLength={72}
            required
          />
          <button type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-arena-600 dark:text-neon-200/80 font-semibold"
                  onClick={() => setShowNew((v) => !v)}>
            {showNew ? t('common.hide') : t('common.show')}
          </button>
        </div>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
          {t('profile.password.rule')}
        </p>
      </div>
      <div>
        <label className="label">{t('profile.password.confirm')}</label>
        <input
          className="input"
          type={showNew ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder={t('profile.password.confirmPh')}
          autoComplete="new-password"
          required
        />
      </div>
      <button className="btn-primary w-full"
              disabled={busy || !currentPassword || newPassword.length < 8 || !confirmPassword}>
        {busy ? t('common.saving') : t('profile.password.editTitle')}
      </button>
    </form>
  );
}

// ─── Telegram link card (supports unlink + re-link) ───────────────
function TelegramCard({ user, onUpdated }: { user: User; onUpdated: () => Promise<void> }) {
  const { push } = useToast();
  const t = useT();
  const linked = !!user.telegram_chat_id;
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);

  const verifiedElsewhere = !!user.email_verified_at;
  const unlinkConsequence = verifiedElsewhere
    ? t('profile.telegram.unlinkVerified')
    : t('profile.telegram.unlinkOnly');

  async function getLink() {
    setBusy(true);
    try {
      const r = await api.post<{ deepLink: string }>('/auth/telegram-link');
      setLink(r.deepLink);
    } catch (e) { push('error', (e as ApiError).message); }
    finally { setBusy(false); }
  }

  async function doUnlink() {
    await api.post('/me/telegram-unlink');
    await onUpdated();
    setLink(null);
    push('success', t('profile.toast.tgUnlinked'));
  }

  return (
    <div className="card p-4">
      <h3 className="font-bold mb-1">{t('profile.telegram.title')}</h3>
      {linked ? (
        <>
          <p className="text-sm text-neutral-600 dark:text-neutral-300">
            {t('profile.telegram.linkedPrefix')} <b>@{user.telegram_username || user.telegram_chat_id}</b>. {t('profile.telegram.otpHere')}
          </p>
          <button className="btn-ghost mt-2 text-xs px-3 py-1.5"
                  onClick={() => setConfirmUnlink(true)} disabled={busy}>
            {t('profile.telegram.unlinkChange')}
          </button>
          <DangerConfirmModal
            open={confirmUnlink}
            onClose={() => setConfirmUnlink(false)}
            onConfirm={doUnlink}
            title={t('profile.telegram.unlinkTitle')}
            description={unlinkConsequence}
            confirmLabel={t('profile.telegram.unlink')}
            cancelLabel={t('profile.telegram.keepLinked')}
          />
        </>
      ) : (
        <>
          <p className="text-sm text-neutral-600 dark:text-neutral-300">
            {t('profile.telegram.notLinkedDesc')}
          </p>
          {link
            ? <a href={link} target="_blank" rel="noreferrer" className="btn-primary mt-2">{t('profile.telegram.openBot')}</a>
            : <button className="btn-ghost mt-2" onClick={getLink} disabled={busy}>
                {busy ? t('profile.telegram.generating') : t('profile.telegram.generate')}
              </button>}
        </>
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <dt className="text-xs text-neutral-500 dark:text-neutral-400">{k}</dt>
      <dd className="font-medium break-words">{v}</dd>
    </div>
  );
}
